<template>
	<div
		:class="[
			'floating-notification',
			disabled && 'is-disabled',
			...stateClassList
		]"
		data-testid="floating-notification"
	>
		<div
			:class="[
				'floating-notification__shell-msg',
				isExpanded && 'is-open'
			]"
			data-testid="floating-notification-msg"
		>
			<slot>
				{{ messageText }}
			</slot>
		</div>
		<button
			class="floating-notification__trigger"
			@click="onClick"
			:aria-label="ariaLabel"
			data-testid="floating-notification-trigger"
		>
			<i :class="['icon', iconClassName]"></i>
		</button>
	</div>
</template>

<script>
import baseComponentMixin from '@/mixins/base-component-mixin';
import componentStateMixin from '@/mixins/component-state-mixin';

export default {
	name: 'FloatingNotification',
	mixins: [baseComponentMixin, componentStateMixin],
	components: {},
	data() {
		return {
			isExpanded: false
		};
	},
	props: {
		ariaLabel: {
			type: String,
			default: ''
		},
		messageText: {
			type: String
		},
		iconClassName: {
			type: String,
			default: 'icon-search'
		},
		/**
		 * Expand the Notification.
		 * @type {Boolean}
		 */
		expanded: {
			type: Boolean,
			default: false
		}
	},
	watch: {
		expanded: {
			immediate: true,
			handler(val) {
				this.isExpanded = val;
			}
		}
	},
	methods: {
		onClick() {
			this.isExpanded = !this.isExpanded;
		}
	}
};
</script>

<style lang="scss" scoped>
/*------------------------------------*\
  #BASE-COMPONENT-STYLES
  The base class and variant classes, if applicable, are never added/removed via dev logic.
\*------------------------------------*/
.floating-notification {
	position: relative;
	display: flex;
	width: 95%;
	justify-content: flex-end;
	margin-left: auto;
	align-items: center;

	&__trigger {
		position: relative;
		height: 100%;
		cursor: pointer;
		border-radius: 50%;
		border: 2px solid $ui-accessible-need;
		background-color: var(--ui-neutral);
		color: $ui-fg-dark;
		height: 34px;
		min-width: 34px;
		@include flex-full-center;
		@include medium-shadow;

		&:hover {
			background-color: var(--ui-neutral-light);
			border-color: var(--ui-neutral);
		}
	}

	&__shell-msg {
		text-align: left;
		font-size: $txt-font-size;
		color: $ui-fg-dark;
		flex-grow: 2;
		padding: $field-padding-y $field-padding-x;
		padding-right: $padding-lg;
		border: $btn-border-width solid var(--ui-neutral);
		background-color: var(--ui-neutral-light);
		opacity: 0;
		margin-right: -16px;
		@include animation(all, 'fast');

		&.is-open {
			opacity: 1;
		}
	}
}

/*------------------------------------*\
  #STATE-TYPE-STYLES
  This section is only for floating-notification CSS change via state type.
  Neutral state is the default, CSS for that is located in the base component styles section.
  These classes are added/removed as needed via dev logic
\*------------------------------------*/
.floating-notification {
	&.is-negative {
		.floating-notification {
			&__shell-msg {
				border-color: var(--ui-negative);
				background-color: var(--ui-negative-light);
			}

			&__trigger {
				background-color: var(--ui-negative);
				color: $ui-fg-light;

				&:hover {
					background-color: var(--ui-negative-light);
					border-color: var(--ui-negative);
					color: $ui-fg-dark;
				}
			}
		}
	}

	&.is-warning {
		.floating-notification {
			&__shell-msg {
				border-color: var(--ui-warning);
				background-color: var(--ui-warning-light);
			}

			&__trigger {
				background-color: var(--ui-warning);
				color: $ui-fg-dark;

				&:hover {
					background-color: var(--ui-warning-light);
					border-color: var(--ui-warning);
				}
			}
		}
	}

	&.is-positive {
		.floating-notification {
			&__shell-msg {
				border-color: var(--ui-positive);
				background-color: var(--ui-positive-light);
			}

			&__trigger {
				background-color: var(--ui-positive);
				color: $ui-fg-dark;

				&:hover {
					background-color: var(--ui-positive-light);
					border-color: var(--ui-positive);
				}
			}
		}
	}
}

// DARK MODE STYLES
[mode='dark'] {
	.floating-notification {
		.is-negative {
			.floating-notification {
				&__trigger {
					color: $ui-fg-dark;
				}
			}
		}
	}
}
</style>
